import 'simplebar-react/dist/simplebar.min.css';

import { NewReleases as NewReleasesIcon } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import NaturePeopleIcon from '@mui/icons-material/NaturePeople';
import {
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as React from 'react';
import { Link, useMatch, useNavigate } from 'react-router-dom';
import SimpleBar from 'simplebar-react';

import { getChipStatusProps } from '@/components/StatusIcon/statusMapping';
import { getWorstDevice } from '@/components/StatusIcon/statusMapping';
import useMixPanel from '@/hooks/useMixPanel';

import { createHomeRoute, createStructureRoute, createUnitRoute, routes } from '../../app-routes';
import { getUserId } from '../../auth';
import StructureForm from '../../Structures/Create';
// Types
import type { Structure } from '../../Structures/types';
import { useStructure, useStructures } from '../../Structures/useStructures';
import { getUnitStatus } from '../../Structures/util';
import { create as createUnit } from '../../Units/api';
import { FormDialog as AddUnitDialog } from '../../Units/FormDialog';
import type { AddUnitFormValues, Unit } from '../../Units/types';
import { naturalCompare } from '../../util';
import { ErrorBoundary } from '../ErrorBoundary';
import { StatusIcon } from '../StatusIcon';
import { DrawerHeader, MiniDrawer } from './components';
import { drawerWidth } from './util';

// Constants //////////////////////////////////////////////////////////////////

const listItemStyles = makeStyles((theme) => ({
  link: {
    textDecoration: 'none',
  },
  listItemText: {
    color: 'white',
  },
  listItemTextPrimary: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  nestedListItem: {
    paddingLeft: theme.spacing(4),
  },
}));

// Structure List Item ////////////////////////////////////////////////////////

type StructureListItemProps = {
  structure: Structure;
  isSelected: boolean;
  numberOfUnits: number;
  onClick: () => void;
  isExpanded: boolean;
};

const StructureListItem = ({
  structure,
  isSelected,
  numberOfUnits,
  onClick,
  isExpanded,
}: StructureListItemProps) => {
  const classes = listItemStyles();

  return (
    <ErrorBoundary>
      <Link to={createStructureRoute(structure.id)} className={classes.link} onClick={onClick}>
        <ListItem button key={structure.name} selected={isSelected}>
          <ListItemIcon>
            <BusinessIcon htmlColor="white" />
          </ListItemIcon>
          <ListItemText
            primary={structure.name}
            secondary={`${numberOfUnits} Units`}
            classes={{
              root: classes.listItemText,
              primary: classes.listItemTextPrimary,
              secondary: classes.listItemText,
            }}
          />
          {isExpanded ? <ExpandLess htmlColor="white" /> : <ExpandMore htmlColor="white" />}
        </ListItem>
      </Link>
    </ErrorBoundary>
  );
};

// Unit List Item /////////////////////////////////////////////////////////////

type UnitListItemProps = {
  structureId: string;
  structureName: string;
  unit: Unit;
  isSelected: boolean;
  sidebarOpen: boolean;
};

interface IChipStatus {
  bgColor: string;
  text: string;
  color: string;
  type: 'default' | 'leak' | 'sensor' | 'connection';
}

export const ChipStatus = ({ bgColor, text, color, type }: IChipStatus) => {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: bgColor,
        color: color,
        borderRadius: '8px',
        fontWeight: 'bold',
        padding: '4px 8px',
        fontSize: '0.875rem',
        textTransform: 'uppercase',
        boxShadow: 1,
        whiteSpace: 'nowrap',
      }}
    >
      <Stack direction={'row'} gap={1}>
        <StatusIcon color={color} type={type} />
        {text !== '' && <Typography fontSize={12}>{text}</Typography>}
      </Stack>
    </Box>
  );
};

const UnitListItem = ({
  structureId,
  structureName: _structureName, // destructuring alias to avoid unused variable warning
  unit,
  isSelected,
  sidebarOpen,
}: UnitListItemProps) => {
  const classes = listItemStyles();
  const unitStatus = getUnitStatus(unit);

  const RenderDeviceStatus = (device, onlyIcon = false) => {
    const props = getChipStatusProps(device.device_status, device.device_status_strings?.[0]);
    if (props) {
      return <ChipStatus {...props} text={onlyIcon ? '' : device.name} />;
    }
    return null;
  };
  return (
    <ErrorBoundary>
      <Link to={createUnitRoute(structureId, unit.id)} className={classes.link} onClick={() => {}}>
        <ListItem
          button
          dense
          key={unit.name}
          className={sidebarOpen ? classes.nestedListItem : ''}
          selected={isSelected}
        >
          <ListItemIcon>
            {unit.unit_flags.length > 0 ? (
              <NaturePeopleIcon htmlColor="white" />
            ) : (
              <MeetingRoomIcon htmlColor="white" />
            )}
          </ListItemIcon>
          <ListItemText
            primary={unit.name}
            classes={{
              root: classes.listItemText,
              primary: classes.listItemTextPrimary,
            }}
          />
          {unitStatus !== 'NO_DEVICES' && unit.devices.length > 1 ? (
            <Stack direction={'row'} gap={0.5} alignItems="center">
              {(() => {
                const worstDevice = getWorstDevice(unit.devices);
                return worstDevice ? RenderDeviceStatus(worstDevice, true) : null;
              })()}
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '4px',
                  padding: '0px 4px',
                  minWidth: '24px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography color="white" fontSize="12px" fontWeight="500">
                  +{unit.devices.length - 1}
                </Typography>
              </Box>
            </Stack>
          ) : (
            unit.devices && unit.devices.map((i) => RenderDeviceStatus(i, true))
          )}
        </ListItem>
      </Link>
    </ErrorBoundary>
  );
};

// Component //////////////////////////////////////////////////////////////////

const useStyles: () => {
  bottomToolbarButtonContainer: string;
  bottomToolbarGradient: string;
  bottomToolbarRoot: string;
  drawer: string;
  homeListItem: string;
  link: string;
  list: string;
  listItemText: string;
  nestedListItem: string;
} = makeStyles((theme) => ({
  bottomToolbarRoot: {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    backgroundColor: 'transparent',
    justifyContent: 'center',
    flexDirection: 'column',
    paddingLeft: 0,
    paddingRight: 0,
  },
  bottomToolbarGradient: {
    backgroundImage: 'linear-gradient(to bottom, rgba(86, 86, 86, 0), rgba(86, 86, 86, 1))',
    height: theme.spacing(2),
    width: '100%',
  },
  bottomToolbarButtonContainer: {
    backgroundColor: 'rgba(86, 86, 86, 1)',
    width: '100%',
    height: theme.spacing(6),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  drawer: {
    maxWidth: drawerWidth,
    minWidth: drawerWidth,
    backgroundColor: theme.palette.grey[800], //'rgb(86, 86, 86)'
  },
  homeListItem: {
    height: theme.spacing(8),
  },
  link: {
    textDecoration: 'none',
  },
  list: {
    paddingTop: 0,
  },
  listItemText: {
    color: 'white',
  },
  nestedListItem: {
    paddingLeft: theme.spacing(4),
  },
}));

interface DrawerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const Drawer = ({ open, setOpen }: DrawerProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const hMatch = useMatch(routes.home);
  const sMatch = useMatch(routes.property);
  const uMatch = useMatch(routes.unit);
  const structureId = sMatch?.params?.structureId || uMatch?.params?.structureId || '';
  const unitId = uMatch?.params?.unitId;
  const { structures } = useStructures();
  const { structure, mutate: updateStructure } = useStructure(structureId);

  const [expandedProperties, setExpandedProperties] = React.useState<Record<string, boolean>>({});
  const [isAddStructureFormOpen, setIsAddStructureFormOpen] = React.useState(false);

  // Clear expanded properties when navigating away from all properties (e.g., to home)
  React.useEffect(() => {
    if (!structureId) {
      setExpandedProperties({});
    }
  }, [structureId]);
  const [type, setType] = React.useState<'UNIT' | 'COMMON_AREA'>('UNIT');
  const [isAddUnitOpen, setIsAddUnitOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const { trackEvent } = useMixPanel();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openMenuUnit = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSubmitAddUnit = async ({
    name,
    pipeDiameter,
    pipeMaterial,
    unitFlag,
  }: AddUnitFormValues) => {
    if (!structure || !structureId) return;
    // eslint-disable-next-line no-console
    console.log('Add Unit', name, pipeDiameter, pipeMaterial, unitFlag);
    setLoading(true);
    try {
      const newUnit = await createUnit(
        await getUserId(),
        structureId,
        structure.address,
        name,
        structure.units.length,
        unitFlag,
      );
      await updateStructure({
        ...structure,
        units: [...structure.units, newUnit],
      });
      trackEvent('Unit Created', {
        PropertyName: structure.name,
        PropertyID: structure.id,
        UnitName: newUnit.name,
        UnitID: newUnit.id,
      });
      setIsAddUnitOpen(false);
    } catch (err) {
      // Error already shown by API layer, keep dialog open
      console.error('Failed to create unit:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ErrorBoundary>
      <MiniDrawer anchor={'left'} variant="permanent" open={open}>
        <DrawerHeader>
          <IconButton onClick={() => setOpen(false)}>
            <ChevronLeftIcon htmlColor="#fff" />
          </IconButton>
        </DrawerHeader>
        <Divider />
        <SimpleBar style={{ maxHeight: 'calc(100vh - 56px - 70px)' }}>
          <List className={classes.list}>
            <Link to={createHomeRoute()} className={classes.link}>
              <ListItem button selected={Boolean(hMatch)} className={classes.homeListItem}>
                <ListItemIcon>
                  <HomeWorkIcon htmlColor="white" />
                </ListItemIcon>
                <ListItemText primary={'Home'} className={classes.listItemText} />
              </ListItem>
            </Link>
            {structures
              ? [...structures]
                  .sort((a, b) => naturalCompare(a.name, b.name))
                  .map((s) => {
                    return (
                      <React.Fragment key={s.id}>
                        <StructureListItem
                          structure={s}
                          numberOfUnits={s.units.length}
                          onClick={() => {
                            setExpandedProperties((prev) => {
                              const isCurrentlyExpanded = prev[s.id];
                              if (isCurrentlyExpanded) {
                                // If clicking on an already expanded property, close it
                                return { [s.id]: false };
                              } else {
                                // If clicking on a collapsed property, close all others and open this one
                                return { [s.id]: true };
                              }
                            });
                          }}
                          isSelected={s.id === structureId}
                          isExpanded={expandedProperties[s.id] || false}
                        />
                        <Collapse in={structureId === s.id && expandedProperties[s.id]}>
                          <List>
                            {s.units &&
                              s.units
                                .filter(Boolean)
                                .sort((a, b) => naturalCompare(a.name, b.name))
                                .map((u) => {
                                  return (
                                    <UnitListItem
                                      key={u.id}
                                      structureId={s.id}
                                      structureName={s.name}
                                      unit={u}
                                      isSelected={u.id === unitId}
                                      sidebarOpen={open}
                                    />
                                  );
                                })}
                            <Button
                              sx={{
                                width: '100%',
                                margin: 0,
                                padding: 0,
                                textTransform: 'capitalize',
                              }}
                              onClick={handleClick}
                            >
                              <ListItem
                                // button
                                // onClick={() => setIsAddUnitOpen(true)}
                                className={open ? classes.nestedListItem : ''}
                              >
                                <ListItemIcon>
                                  <AddIcon htmlColor="white" />
                                </ListItemIcon>
                                <ListItemText
                                  primary="Add"
                                  classes={{
                                    root: classes.listItemText,
                                  }}
                                />
                              </ListItem>
                            </Button>
                          </List>
                        </Collapse>
                        <Menu
                          id="basic-menu"
                          anchorEl={anchorEl}
                          open={openMenuUnit}
                          onClose={handleClose}
                          MenuListProps={{
                            'aria-labelledby': 'basic-button',
                          }}
                        >
                          <MenuItem
                            onClick={() => {
                              handleClose();
                              setIsAddUnitOpen(true);
                              setType('UNIT');
                            }}
                          >
                            <MeetingRoomIcon htmlColor="black" sx={{ marginRight: 2 }} />
                            Add Unit
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              handleClose();
                              setIsAddUnitOpen(true);
                              setType('COMMON_AREA');
                            }}
                          >
                            <NaturePeopleIcon htmlColor="black" sx={{ marginRight: 2 }} />
                            Add Other
                          </MenuItem>
                        </Menu>
                      </React.Fragment>
                    );
                  })
              : null}
            <ListItem button onClick={() => setIsAddStructureFormOpen(true)}>
              <ListItemIcon>
                <AddIcon htmlColor="white" />
              </ListItemIcon>
              <ListItemText primary="Add Property" className={classes.listItemText} />
            </ListItem>
          </List>
        </SimpleBar>
        <Box sx={{ position: 'absolute', bottom: 16, left: 16 }}>
          <Tooltip title={`${process.env.REACT_APP_VERSION ?? 'unknown'}`}>
            <IconButton
              onClick={() => navigate('/release-notes')}
              sx={{
                padding: 1,
                color: 'white',
              }}
            >
              <NewReleasesIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <StructureForm
          open={isAddStructureFormOpen}
          onClose={() => setIsAddStructureFormOpen(false)}
        />
        <AddUnitDialog
          addOrEdit={'ADD'}
          type={type}
          name={`${(structure?.units.length || 0) + 1}`}
          pipeDiameter={1}
          pipeMaterial={'COPPER-M'}
          open={isAddUnitOpen}
          onClose={() => setIsAddUnitOpen(false)}
          onSubmit={handleSubmitAddUnit}
          loading={loading}
        />
      </MiniDrawer>
    </ErrorBoundary>
  );
};
